"use client";

import { Link } from "@/components/ui/Link";
import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { SplitHeading } from "@/components/ui/SplitHeading";
import { SECTION_TITLE_CLASS } from "@/lib/typography";
import { fadeScale, staggerContainer, useMotionSafe } from "@/lib/motion";
import type { Service } from "@/lib/content/types";

type WhatWeDoProps = {
  services: Service[];
  label?: string;
  title?: string;
  description?: string;
};

export function WhatWeDo({
  services,
  label,
  title,
  description,
}: WhatWeDoProps) {
  const { transition, viewport, prefersReducedMotion } = useMotionSafe();

  if (!services.length || !title) return null;

  return (
    <section id="what-we-do" className="py-20 md:py-28 lg:py-32">
      <Container>
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start lg:gap-16 xl:gap-20">
          <div className="lg:sticky lg:top-28 lg:col-span-4 lg:self-start">
            <Reveal>
              {label ? <SectionLabel className="mb-5">{label}</SectionLabel> : null}
              <SplitHeading className={SECTION_TITLE_CLASS}>
                {title}
              </SplitHeading>
              {description ? (
                <p className="mt-6 max-w-sm text-base leading-relaxed text-muted md:text-lg">
                  {description}
                </p>
              ) : null}
            </Reveal>
          </div>

          <motion.div
            className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:col-span-8 lg:gap-x-12 lg:gap-y-14"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={prefersReducedMotion ? undefined : staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.slug}
                variants={prefersReducedMotion ? undefined : fadeScale}
                transition={transition}
              >
                <Link
                  href={service.href}
                  className="group block"
                >
                  <span className="mb-4 block font-mono text-xs text-muted/60">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-lg font-medium tracking-tight transition-colors group-hover:text-white md:text-xl">
                    {service.title}
                  </h3>
                  {service.description ? (
                    <p className="mt-3 text-sm leading-relaxed text-muted md:text-[0.9375rem]">
                      {service.description}
                    </p>
                  ) : null}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
